/**
 * Manter o histórico no fim quando ele cresce (H04).
 *
 * ── O que a versão anterior fazia, e por que não bastava ──────────────────
 *
 * Ela rolava quando o ID da última mensagem mudava. Parece suficiente e não é,
 * por três razões que aparecem justamente no uso real:
 *
 *   1. **conteúdo que cresce depois.** A bolha entra na tela antes de a foto
 *      carregar. No instante em que rolamos, a altura ainda é a do lugar vazio
 *      — e quando a imagem chega, ela empurra a mensagem para fora da vista.
 *      É o caso mais comum numa revenda, que troca foto o dia inteiro;
 *
 *   2. **mensagem que chega com um envio pendente na fila.** O id observado
 *      era o do envio local quando existia um; enquanto ele não confirmava, a
 *      chegada do cliente não mudava o id observado, e nada rolava;
 *
 *   3. **rolar sempre é pior.** Quem está lendo o histórico lá em cima seria
 *      arrancado de lá a cada mensagem nova.
 *
 * ── A regra, que é a do WhatsApp ──────────────────────────────────────────
 *
 * Se a pessoa está NO FIM, o fim a acompanha: qualquer crescimento — mensagem
 * nova, foto que carregou, bolha que mudou de altura — mantém o fim visível.
 *
 * Se ela subiu para ler, nada se move. A mensagem nova chega em silêncio, e
 * quem avisa é a região viva do `anuncio.tsx`, que já existe para isso.
 *
 * ── Por que `ResizeObserver`, e não um efeito por mensagem ────────────────
 *
 * Porque o gatilho certo não é "chegou mensagem", é "o conteúdo ficou mais
 * alto". Os dois coincidem na maioria das vezes e divergem exatamente nos
 * casos que quebravam: a foto que carrega meio segundo depois, o áudio que
 * ganha altura ao descobrir a duração, a bolha que quebra em duas linhas
 * quando a fonte carrega.
 */

/** Quão perto do fim ainda conta como "está no fim". */
const MARGEM_DO_FIM = 120;

export function pertoDoFim(
  elemento: { scrollTop: number; scrollHeight: number; clientHeight: number },
  margem = MARGEM_DO_FIM,
): boolean {
  const distancia = elemento.scrollHeight - elemento.scrollTop - elemento.clientHeight;
  /*
   * Distância negativa acontece — rolagem elástica no macOS e no toque passa
   * do fim. Sem o `Math.max`, o `-40` de um puxão para baixo seria lido como
   * "está no fim" por acaso, e não por medida.
   */
  return Math.max(distancia, 0) <= margem;
}

/**
 * Gruda o elemento no fim enquanto o conteúdo cresce.
 *
 * Devolve a função que desliga tudo. Sem argumentos além do elemento: quem
 * decide se deve grudar é a posição atual, medida a cada mudança.
 */
export function grudarNoFim(elemento: HTMLElement): () => void {
  let noFim = true;

  const aoRolar = (): void => {
    noFim = pertoDoFim(elemento);
  };

  const irAoFim = (): void => {
    if (!noFim) return;
    /*
     * `scrollTop` direto, e não `scrollTo({ behavior: 'smooth' })`.
     *
     * A rolagem suave leva centenas de milissegundos, e uma segunda mensagem
     * chegando no meio dela cancela a primeira — o efeito é a lista tremendo e
     * parando no lugar errado. Aqui o objetivo é estar no fim, não a animação
     * de chegar lá.
     */
    elemento.scrollTop = elemento.scrollHeight;
  };

  /*
   * Abrir uma conversa cai no FIM, sempre.
   *
   * É onde a conversa está: o assunto de agora é a última mensagem, não a
   * primeira de dois anos atrás. Abrir no topo obrigaria a rolar até embaixo
   * a cada conversa aberta — e abrir no meio, que era o que acontecia quando o
   * conteúdo crescia depois, é pior ainda: não é nem um lugar nem o outro.
   */
  elemento.scrollTop = elemento.scrollHeight;

  elemento.addEventListener('scroll', aoRolar, { passive: true });

  const observador =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => irAoFim());

  // O observador olha os FILHOS: o container tem altura fixa (é ele que rola),
  // e quem cresce é o conteúdo dentro dele.
  if (observador) {
    for (const filho of Array.from(elemento.children)) observador.observe(filho);
  }

  /*
   * Filho novo também precisa ser observado.
   *
   * Cada mensagem é um filho direto, e sem isto só as bolhas presentes na
   * montagem seriam acompanhadas — o que faz a rolagem funcionar na abertura e
   * parar de funcionar dali em diante, que é o pior dos dois mundos.
   */
  const mutacoes =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((registros) => {
          for (const registro of registros) {
            for (const no of Array.from(registro.addedNodes)) {
              if (observador && no instanceof Element) observador.observe(no);
            }
          }
          irAoFim();
        });

  mutacoes?.observe(elemento, { childList: true });

  return () => {
    elemento.removeEventListener('scroll', aoRolar);
    observador?.disconnect();
    mutacoes?.disconnect();
  };
}
